import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthWrapper';
import { useWallet } from '@/contexts/WalletContext';
import { AnimatePresence, motion } from 'framer-motion';
import DropMenu from './DropMenu';
import styles from '@/styles/components/navigation/TopBar.module.css';

/**
 * TopBar component props interface
 */
export interface TopBarProps {
  /** Whether to show a border at the bottom */
  showBorder?: boolean;
  /** Mobile: whether viewport is mobile (show burger) */
  isMobile?: boolean;
  /** Mobile: open the sidebar drawer */
  onOpenMobileMenu?: () => void;
  /** Additional CSS class names */
  className?: string;
  /** Custom styles */
  style?: React.CSSProperties;
}

/**
 * Generate a consistent color from a string
 */
function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return '#' + '00000'.substring(0, 6 - c.length) + c;
}

/**
 * Avatar component for user profile display
 */
interface AvatarProps {
  username: string;
  imageUrl?: string;
  size?: number;
}

const Avatar: React.FC<AvatarProps> = ({ username, imageUrl, size = 32 }) => {
  const avatarBg = stringToColor(username);
  const firstLetter = username[0]?.toUpperCase() || 'U';

  const avatarStyle: React.CSSProperties = {
    width: size,
    height: size,
  };

  if (imageUrl) {
    return (
      <div
        className={styles.avatar}
        style={{ ...avatarStyle, background: '#202020' }}
      >
        <img
          src={imageUrl}
          alt={`${username} avatar`}
          className={styles.avatarImage}
          style={{ width: size, height: size }}
        />
      </div>
    );
  }

  return (
    <div
      className={styles.avatar}
      style={{
        ...avatarStyle,
        background: avatarBg,
        fontSize: Math.round(size * 0.5),
      }}
    >
      <span className={styles.avatarText}>{firstLetter}</span>
    </div>
  );
};

/**
 * TopBar component following design system standards
 * Provides navigation and user profile access
 *
 * @example
 * ```tsx
 * <TopBar
 *   onToggleSidebar={handleToggle}
 *   isSidebarVisible={sidebarVisible}
 *   showBorder={true}
 * />
 * ```
 */
const TopBar: React.FC<TopBarProps> = ({
  showBorder = false,
  isMobile = false,
  onOpenMobileMenu,
  className,
  style,
}) => {
  const { user, logout } = useAuth();
  const { state: walletState } = useWallet();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const username = user?.name || 'Username';
  const email = user?.email || 'username@gmail.com';
  const userImage = (user as any)?.imageUrl;

  // Get actual wallet balance from smart contract
  const walletBalance =
    walletState.isConnected && walletState.swmBalance.pool
      ? `${parseFloat(walletState.swmBalance.pool).toFixed(2)} USD`
      : '0.00 USD';
  const trialBalance = walletState.trialBalance || 0;
  const hasTrialBalance = trialBalance > 0;

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const toggleDropdown = useCallback(() => {
    setDropdownOpen(prev => !prev);
  }, []);

  const handleDropdownAction = useCallback((action: () => void) => {
    setDropdownOpen(false);
    action();
  }, []);

  const topBarClassName =
    `${styles.topBar} ${showBorder ? styles.withBorder : ''} ${className || ''}`.trim();
  const dropdownIconClassName = `${styles.dropdownIcon} ${dropdownOpen ? styles.open : styles.closed}`;

  return (
    <header style={style} className={topBarClassName}>
      <div className={styles.contentRow}>
        {/* Mobile: burger button to open sidebar */}
        {isMobile && onOpenMobileMenu && (
          <button
            type="button"
            className={styles.burgerButton}
            onClick={onOpenMobileMenu}
            aria-label="Open menu"
          >
            <span className={styles.burgerLine} />
            <span className={styles.burgerLine} />
            <span className={styles.burgerLine} />
          </button>
        )}
        {/* Actions slot that pages can render into via portal */}
        <div id="topbar-slot" className={styles.actionsSlot} />

        {/* User profile section */}
        <div ref={dropdownRef} className={styles.userSection}>
        <button
          onClick={toggleDropdown}
          className={styles.userButton}
          aria-label="User menu"
          aria-expanded={dropdownOpen}
        >
          <Avatar username={username} imageUrl={userImage} size={32} />
          <span className={styles.username}>{username}</span>
          <AnimatePresence>
            {hasTrialBalance && (
              <motion.span
                key={Math.round(trialBalance)}
                className={styles.trialBadge}
                initial={{ scale: 0.6, opacity: 0, y: -4 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 320, damping: 22 }}
                data-testid="trial-balance-badge"
              >
                {Math.round(trialBalance)} trial
              </motion.span>
            )}
          </AnimatePresence>
          <img
            src="/icons/dropdown.svg"
            alt=""
            className={dropdownIconClassName}
            aria-hidden="true"
          />
        </button>

        {/* Dropdown menu */}
        {dropdownOpen && (
          <div className={styles.dropdownContainer}>
            <DropMenu
              username={username}
              email={email}
              walletBalance={walletBalance}
              trialBalance={hasTrialBalance ? trialBalance : undefined}
              onProfile={() =>
                handleDropdownAction(() => {
                  navigate('/profile?tab=profile');
                })
              }
              onWallet={() =>
                handleDropdownAction(() => {
                  navigate('/profile?tab=wallet');
                })
              }
              onHistory={() =>
                handleDropdownAction(() => {
                  navigate('/profile?tab=history');
                })
              }
              onLogout={() => handleDropdownAction(logout)}
            />
          </div>
        )}
        </div>
      </div>
    </header>
  );
};

TopBar.displayName = 'TopBar';

export default TopBar;
